import axiosInstance, { endpoints } from "../../lib/axios";

import type {
  IInköp,
  IInköpsMatchning,
  InköpParsningResponse
} from "../../types/nya/ekonomi/inkop/InkopTyper";

export interface IInköpsFörslag {
  id: string;
  orgId: string;
  uppladdatAv: string;
  filnamn: string;
  status: string;
  uppladdadVid: string;
  url: string;
  felmeddelande: string;
  underlagId?: string;
}

export function HämtaBokföringAvInköp(orgId: string): Promise<InköpParsningResponse> {
  const url = endpoints.inköp.matchningar(orgId);
  return axiosInstance.get(url)
    .then(response => response.data);
}

export function HämtaOmatchadeInköp(orgId: string): Promise<IInköp[]> {
  const url = endpoints.inköp.omatchadeInköp(orgId);
  return axiosInstance.get(url)
    .then(response => response.data);
}

export function HämtaOmatchadeUnderlagCount(orgId: string): Promise<number> {
  const url = endpoints.inköp.underlagCount(orgId);
  return axiosInstance.get(url)
    .then(response => response.data);
}

/** Lättvikts-GET: returnerar underlag + inköp utan matchningskandidater. Lämplig för polling. */
export function HämtaMatchningar(orgId: string): Promise<InköpParsningResponse> {
  const url = endpoints.inköp.matchningar(orgId);
  return axiosInstance.get(url)
    .then(response => response.data);
}

/**
 * Explicit, on-demand matchningsberäkning (POST): kör tier-matchningsalgoritmen och returnerar
 * matchningskandidater per underlag. Anropas när matchningsmodalen öppnas – aldrig i pollingsloopen.
 */
export function beräknaMatchningar(orgId: string): Promise<InköpParsningResponse> {
  const url = endpoints.inköp.beräknaMatchningar(orgId);
  return axiosInstance.post(url)
    .then(response => response.data);
}

export function HämtaOmatchadeUnderlag(orgId: string): Promise<any[]> {
  const url = endpoints.inköp.omatchadeUnderlag(orgId);
  return axiosInstance.get(url)
    .then(response => response.data);
}

/**
 * Alla underlag inklusive bokförda, med `bokförd` per underlag. Till skillnad från
 * `HämtaOmatchadeUnderlag` går det här att skilja en betald faktura från en borttagen.
 */
export function HämtaAllaUnderlag(orgId: string): Promise<any[]> {
  const url = endpoints.inköp.allaUnderlag(orgId);
  return axiosInstance.get(url)
    .then(response => response.data);
}

export function HämtaMatchningFörTransaktion(orgId: string, banktransaktionId: string): Promise<IInköpsMatchning[]> {
  const url = endpoints.inköp.matchningForTransaktion(orgId, banktransaktionId);
  return axiosInstance.get(url)
    .then(response => response.data);
}

export function matchaManuellt(orgId: string, inköpsUnderlagId: string, inköpId: string, bekräftatKreditering?: boolean, skallBokföras?: boolean, affärshändelse?: IAffärshändelse): Promise<InköpParsningResponse> {
  const url = `${endpoints.inköp.hämta(orgId)}/matcha-manuellt`;
  return axiosInstance.post(url, {
    inköpsUnderlagId,
    inköpId,
    bekräftatKreditering,
    skallBokföras,
    affärshändelse
  })
    .then(response => response.data);
}

export function försökMatchaIgen(orgId: string, inköpsUnderlagId: string): Promise<any> {
    const url = `${endpoints.inköp.hämta(orgId)}/matcha-igen`;
    return axiosInstance.post(url, { inköpsUnderlagId })
      .then(response => response.data);
}

export function bokforSomLeverantörsfaktura(orgId: string, inköpsUnderlagId: string): Promise<any> {
  const url = `${endpoints.inköp.hämta(orgId)}/bokfor-som-levfaktura`;
  return axiosInstance.post(url, { inköpsUnderlagId })
    .then(response => response.data);
}

export function laddaUppFilerParallellt(filer: File[], orgId: string): Promise<IInköpsFörslag[]> {
  const formData = new FormData();
  for (let i = 0; i < filer.length; i++) {
    const fil = filer[i];
    formData.append('inköpsFiler', fil);
  }
  return axiosInstance.post(endpoints.inköp.uppladdaUnderlag(orgId), formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
    .then(response => response.data);
}

export function hämtaStatusFörAlla(ids: string[]): Promise<IInköpsFörslag[]> {
  return axiosInstance.post(endpoints.inköp.uppladdaUnderlagStatus, ids)
    .then(response => response.data);
}

export function tolkaIgen(orgId: string, forslagId: string): Promise<void> {
  const url = `${endpoints.inköp.uppladdaUnderlag(orgId)}/tolka-igen/${forslagId}`;
  return axiosInstance.post(url).then(response => response.data);
}

import { IAffärshändelse } from "../../types/nya/ekonomi/bokforing/bokforingstyper";

// Delas med KundfakturaMatchningModal – ligger kvar på den generella affärshändelse-endpointen.
export function sparaAffärshändelse(orgId: string, affärshändelse: IAffärshändelse, bekräftatKreditering?: boolean, skallBokföras?: boolean, banktransaktionId?: string): Promise<any> {
  const url = endpoints.bokföring.affärshändelser.spara(orgId);
  return axiosInstance.post(url, { affärshändelse, bekräftatKreditering, skallBokföras, banktransaktionId })
    .then(response => response.data);
}

/**
 * Inköpsspecifik spara-funktion: anropar inköp V2-endpointen (POST /api/inkop/v2/{orgId}/spara)
 * med ett valfritt inköpsUnderlagId. När det är satt och skallBokföras=true markerar backend
 * det uppladdade underlaget som färdigbearbetat, så att det faller bort ur "att bokföra"-kön.
 */
export function sparaInköpsAffärshändelse(
  orgId: string,
  affärshändelse: IAffärshändelse,
  bekräftatKreditering?: boolean,
  skallBokföras?: boolean,
  inköpsUnderlagId?: string
): Promise<any> {
  const url = endpoints.inköp.spara(orgId);
  return axiosInstance.post(url, { affärshändelse, bekräftatKreditering, skallBokföras, inköpsUnderlagId })
    .then(response => response.data);
}

export function skapaAffärshändelseUtkast(
  orgId: string,
  inköpId: string,
  bekräftatKreditering?: boolean,
  underlagId?: string
): Promise<any> {
  const url = endpoints.inköp.draft(orgId);
  return axiosInstance.post(url, { inköpId, bekräftatKreditering, underlagId })
    .then(response => response.data);
}

/**
 * Hämtar den befintliga (ev. redan bokförda) affärshändelsen för ett underlag som upptäckts som
 * dubblett. Returnerar null om ingen affärshändelse finns för underlaget (HTTP 204).
 */
export function hämtaDubblettAffärshändelse(orgId: string, underlagId: string): Promise<IAffärshändelse | null> {
  const url = endpoints.inköp.dubblettAffärshändelse(orgId, underlagId);
  return axiosInstance.get(url)
    .then(response => (response.status === 204 ? null : (response.data as IAffärshändelse)));
}

export function raderaAllaInköp(orgId: string): Promise<void> {
  const url = `${endpoints.inköp.root}/${orgId}`;
  return axiosInstance.delete(url);
}
